// client/src/coms/views/canvas_parts/canvas_room_view_parts/pencilUtils.js
import { clampOpacity, clampSoftness } from './toolState';
import { markCanvasDirty } from './canvasUtils';
import { clipContextToSelection } from './selectionUtils';
import { getSharedCanvasToolWebglSurface } from './layerUtils';

const brushTipCache = new Map();
const MAX_BRUSH_TIP_CACHE_SIZE = 64;
const PENCIL_TIP_VARIANTS_PER_STROKE = 16;

const gpuPencilStates = typeof WeakMap !== 'undefined' ? new WeakMap() : null;

function createGpuPencilShader(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function createGpuPencilProgram(gl) {
  const vertexSource = `
    attribute vec2 a_center;
    attribute vec2 a_corner;
    attribute vec2 a_dimensions;
    attribute float a_rotation;
    attribute float a_alpha;
    uniform vec2 u_resolution;
    uniform vec2 u_origin;
    varying vec2 v_uv;
    varying float v_alpha;
    void main() {
      float c = cos(a_rotation);
      float s = sin(a_rotation);
      vec2 local = a_corner * a_dimensions * 0.5;
      vec2 rotated = vec2(
        local.x * c - local.y * s,
        local.x * s + local.y * c
      );
      vec2 position = (a_center - u_origin) + rotated;
      vec2 zeroToOne = position / u_resolution;
      vec2 clipSpace = zeroToOne * 2.0 - 1.0;
      gl_Position = vec4(clipSpace * vec2(1.0, -1.0), 0.0, 1.0);
      v_uv = (a_corner * 0.5) + 0.5;
      v_alpha = a_alpha;
    }
  `;

  const fragmentSource = `
    precision mediump float;
    uniform sampler2D u_tipTexture;
    uniform vec3 u_colour;
    varying vec2 v_uv;
    varying float v_alpha;
    void main() {
      vec4 tip = texture2D(u_tipTexture, v_uv);
      float alpha = tip.a * v_alpha;
      if (alpha <= 0.001) discard;
      gl_FragColor = vec4(u_colour, alpha);
    }
  `;

  const vertexShader = createGpuPencilShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragmentShader = createGpuPencilShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  if (!vertexShader || !fragmentShader) return null;

  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }

  return {
    program,
    centerLocation: gl.getAttribLocation(program, 'a_center'),
    cornerLocation: gl.getAttribLocation(program, 'a_corner'),
    dimensionsLocation: gl.getAttribLocation(program, 'a_dimensions'),
    rotationLocation: gl.getAttribLocation(program, 'a_rotation'),
    alphaLocation: gl.getAttribLocation(program, 'a_alpha'),
    resolutionLocation: gl.getUniformLocation(program, 'u_resolution'),
    originLocation: gl.getUniformLocation(program, 'u_origin'),
    tipTextureLocation: gl.getUniformLocation(program, 'u_tipTexture'),
    colourLocation: gl.getUniformLocation(program, 'u_colour'),
  };
}

function resizeGpuPencilState(state, targetCanvas) {
  if (!state || !targetCanvas) return false;
  const width = Math.max(1, Number(targetCanvas.width) || 1);
  const height = Math.max(1, Number(targetCanvas.height) || 1);
  if (state.canvas.width !== width) state.canvas.width = width;
  if (state.canvas.height !== height) state.canvas.height = height;
  state.gl.viewport(0, 0, width, height);
  return true;
}

function destroyGpuPencilState(state) {
  if (!state || !state.gl) return;
  const gl = state.gl;
  if (state.vertexBuffer) gl.deleteBuffer(state.vertexBuffer);
  if (state.programInfo && state.programInfo.program) gl.deleteProgram(state.programInfo.program);
  if (state.textureCache) {
    state.textureCache.forEach((entry) => {
      if (entry && entry.texture) gl.deleteTexture(entry.texture);
    });
    state.textureCache.clear();
  }
}

function gpuPencilCanvasCopiesWithTransparency(state) {
  if (!state || !state.canvas || !state.gl || typeof document === 'undefined') return false;

  const gl = state.gl;
  const previousWidth = state.canvas.width;
  const previousHeight = state.canvas.height;

  state.canvas.width = 1;
  state.canvas.height = 1;
  gl.viewport(0, 0, 1, 1);
  gl.clearColor(1, 1, 1, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.flush();

  const probeCanvas = document.createElement('canvas');
  probeCanvas.width = 1;
  probeCanvas.height = 1;
  const probeContext = probeCanvas.getContext('2d', { willReadFrequently: true });
  if (!probeContext) {
    state.canvas.width = previousWidth;
    state.canvas.height = previousHeight;
    gl.viewport(0, 0, previousWidth, previousHeight);
    return false;
  }

  probeContext.fillStyle = '#ffffff';
  probeContext.fillRect(0, 0, 1, 1);

  try {
    probeContext.drawImage(state.canvas, 0, 0);
    const pixel = probeContext.getImageData(0, 0, 1, 1).data;
    state.canvas.width = previousWidth;
    state.canvas.height = previousHeight;
    gl.viewport(0, 0, previousWidth, previousHeight);
    return pixel[0] > 245 && pixel[1] > 245 && pixel[2] > 245 && pixel[3] > 245;
  } catch {
    state.canvas.width = previousWidth;
    state.canvas.height = previousHeight;
    gl.viewport(0, 0, previousWidth, previousHeight);
    return false;
  }
}


function getGpuPencilState(targetCanvas) {
  if (!targetCanvas || typeof document === 'undefined' || !gpuPencilStates) return null;
  const existing = gpuPencilStates.get(targetCanvas);
  if (existing && existing.canvas && existing.gl && existing.programInfo && existing.programInfo.program) {
    resizeGpuPencilState(existing, targetCanvas);
    return existing;
  }

  const surface = getSharedCanvasToolWebglSurface(
    Math.max(1, Number(targetCanvas.width) || 1),
    Math.max(1, Number(targetCanvas.height) || 1),
  );
  const canvas = surface && surface.canvas;
  const gl = surface && surface.gl;
  if (!canvas || !gl) return null;

  const programInfo = createGpuPencilProgram(gl);
  if (!programInfo || !programInfo.program) return null;

  const state = {
    canvas,
    gl,
    programInfo,
    vertexBuffer: gl.createBuffer(),
    textureCache: new Map(),
    commitCanvas: document.createElement('canvas'),
    supportsTransparentCanvasCopy: undefined,
  };

  resizeGpuPencilState(state, targetCanvas);
  state.supportsTransparentCanvasCopy = gpuPencilCanvasCopiesWithTransparency(state);
  gpuPencilStates.set(targetCanvas, state);
  return state;
}

function getGpuPencilTexture(state, stampSeed) {
  if (!state || !state.gl) return null;
  const key = String(stampSeed >>> 0);
  if (state.textureCache.has(key)) {
    return state.textureCache.get(key);
  }

  const gl = state.gl;
  const tipCanvas = getBrushTipCanvas(128, stampSeed);
  if (!tipCanvas) return null;

  const texture = gl.createTexture();
  if (!texture) return null;
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, tipCanvas);

  const entry = { texture, width: tipCanvas.width, height: tipCanvas.height };
  if (state.textureCache.size >= 128) {
    const oldestKey = state.textureCache.keys().next().value;
    if (oldestKey) {
      const oldest = state.textureCache.get(oldestKey);
      if (oldest && oldest.texture) gl.deleteTexture(oldest.texture);
      state.textureCache.delete(oldestKey);
    }
  }
  state.textureCache.set(key, entry);
  return entry;
}

function buildGpuPencilVertexData(stamps) {
  const source = Array.isArray(stamps) ? stamps.filter(Boolean) : [];
  if (!source.length) return new Float32Array(0);

  const corners = [
    -1, -1,
     1, -1,
     1,  1,
    -1, -1,
     1,  1,
    -1,  1,
  ];
  const floatsPerVertex = 9;
  const verticesPerStamp = 6;
  const data = new Float32Array(source.length * verticesPerStamp * floatsPerVertex);
  let offset = 0;

  for (let stampIndex = 0; stampIndex < source.length; stampIndex += 1) {
    const stamp = source[stampIndex];
    const point = stamp.point || { x: 0, y: 0 };
    const centerX = Number(point.x) || 0;
    const centerY = Number(point.y) || 0;
    const width = Math.max(1, (Number(stamp.size) || 1) * Math.max(0.01, Number(stamp.scaleX) || 1));
    const height = Math.max(1, (Number(stamp.size) || 1) * Math.max(0.01, Number(stamp.scaleY) || 1));
    const rotation = Number(stamp.rotation) || 0;
    const alpha = Math.max(0, Math.min(1, Number(stamp.alpha) || 0));

    for (let vertexIndex = 0; vertexIndex < verticesPerStamp; vertexIndex += 1) {
      const cornerOffset = vertexIndex * 2;
      data[offset] = centerX;
      data[offset + 1] = centerY;
      data[offset + 2] = corners[cornerOffset];
      data[offset + 3] = corners[cornerOffset + 1];
      data[offset + 4] = width;
      data[offset + 5] = height;
      data[offset + 6] = rotation;
      data[offset + 7] = alpha;
      data[offset + 8] = 0;
      offset += floatsPerVertex;
    }
  }

  return data;
}

function getPencilStampsBounds(stamps, targetCanvas) {
  if (!Array.isArray(stamps) || !stamps.length || !targetCanvas) return null;

  const canvasWidth = Math.max(1, Number(targetCanvas.width) || 1);
  const canvasHeight = Math.max(1, Number(targetCanvas.height) || 1);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let index = 0; index < stamps.length; index += 1) {
    const stamp = stamps[index];
    const point = stamp && stamp.point ? stamp.point : null;
    const x = Number(point && point.x);
    const y = Number(point && point.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

    const size = Math.max(1, Number(stamp.size) || 1);
    const scaleX = Math.max(0.01, Number(stamp.scaleX) || 1);
    const scaleY = Math.max(0.01, Number(stamp.scaleY) || 1);
    const radius = (Math.max(size * scaleX, size * scaleY) * 0.5) + 3;

    minX = Math.min(minX, x - radius);
    minY = Math.min(minY, y - radius);
    maxX = Math.max(maxX, x + radius);
    maxY = Math.max(maxY, y + radius);
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) return null;

  const x = Math.max(0, Math.floor(minX));
  const y = Math.max(0, Math.floor(minY));
  const right = Math.min(canvasWidth, Math.ceil(maxX));
  const bottom = Math.min(canvasHeight, Math.ceil(maxY));
  const width = Math.max(0, right - x);
  const height = Math.max(0, bottom - y);

  if (width <= 0 || height <= 0) return null;
  return { x, y, width, height };
}

let pencilColourParserContext = null;

function resolvePencilColourRgb(value) {
  const fallback = [0, 0, 0];
  if (typeof document === 'undefined') return fallback;

  if (!pencilColourParserContext) {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    pencilColourParserContext = canvas.getContext('2d', { willReadFrequently: true });
  }

  const context = pencilColourParserContext;
  if (!context) return fallback;

  context.clearRect(0, 0, 1, 1);
  context.fillStyle = '#000000';
  try {
    context.fillStyle = typeof value === 'string' && value.trim() ? value : '#000000';
  } catch {
    context.fillStyle = '#000000';
  }
  context.fillRect(0, 0, 1, 1);
  const pixel = context.getImageData(0, 0, 1, 1).data;
  return [pixel[0] / 255, pixel[1] / 255, pixel[2] / 255];
}

function renderPencilStampsWithGpu(ctx, stamps, debugOptions = null) {
  if (!ctx || !ctx.canvas || !Array.isArray(stamps) || !stamps.length) return false;

  if (stamps.some((stamp) => stamp && stamp.isEraser)) return false;

  const bounds = getPencilStampsBounds(stamps, ctx.canvas);
  if (!bounds) return false;

  const state = getGpuPencilState(ctx.canvas);
  if (!state || !state.gl || !state.programInfo || !state.vertexBuffer) return false;

  if (state.canvas.width !== bounds.width) state.canvas.width = bounds.width;
  if (state.canvas.height !== bounds.height) state.canvas.height = bounds.height;

  const groupedStamps = new Map();
  for (let index = 0; index < stamps.length; index += 1) {
    const stamp = stamps[index];
    if (!stamp) continue;
    const key = String((stamp.stampSeed || 0) >>> 0);
    if (!groupedStamps.has(key)) groupedStamps.set(key, []);
    groupedStamps.get(key).push(stamp);
  }

  const gl = state.gl;
  const { program, centerLocation, cornerLocation, dimensionsLocation, rotationLocation, alphaLocation, resolutionLocation, originLocation, tipTextureLocation, colourLocation } = state.programInfo;
  gl.viewport(0, 0, bounds.width, bounds.height);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.CULL_FACE);
  gl.disable(gl.SCISSOR_TEST);
  gl.enable(gl.BLEND);
  gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(program);
  gl.uniform2f(resolutionLocation, bounds.width, bounds.height);
  gl.uniform2f(originLocation, bounds.x, bounds.y);
  gl.uniform1i(tipTextureLocation, 0);
  const pencilColour = resolvePencilColourRgb(debugOptions && (debugOptions.color || debugOptions.strokeColor));
  gl.uniform3f(colourLocation, pencilColour[0], pencilColour[1], pencilColour[2]);
  gl.bindBuffer(gl.ARRAY_BUFFER, state.vertexBuffer);

  const stride = 9 * Float32Array.BYTES_PER_ELEMENT;
  gl.enableVertexAttribArray(centerLocation);
  gl.vertexAttribPointer(centerLocation, 2, gl.FLOAT, false, stride, 0);
  gl.enableVertexAttribArray(cornerLocation);
  gl.vertexAttribPointer(cornerLocation, 2, gl.FLOAT, false, stride, 2 * Float32Array.BYTES_PER_ELEMENT);
  gl.enableVertexAttribArray(dimensionsLocation);
  gl.vertexAttribPointer(dimensionsLocation, 2, gl.FLOAT, false, stride, 4 * Float32Array.BYTES_PER_ELEMENT);
  gl.enableVertexAttribArray(rotationLocation);
  gl.vertexAttribPointer(rotationLocation, 1, gl.FLOAT, false, stride, 6 * Float32Array.BYTES_PER_ELEMENT);
  gl.enableVertexAttribArray(alphaLocation);
  gl.vertexAttribPointer(alphaLocation, 1, gl.FLOAT, false, stride, 7 * Float32Array.BYTES_PER_ELEMENT);

  groupedStamps.forEach((group, key) => {
    const textureEntry = getGpuPencilTexture(state, Number(key) || 0);
    if (!textureEntry || !textureEntry.texture) return;
    const vertexData = buildGpuPencilVertexData(group);
    if (!vertexData.length) return;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, textureEntry.texture);
    gl.bufferData(gl.ARRAY_BUFFER, vertexData, gl.STREAM_DRAW);
    gl.drawArrays(gl.TRIANGLES, 0, vertexData.length / 9);
  });
  gl.flush();

  const committed = commitGpuPencilCanvasToLayer(ctx, state, bounds);
  if (!committed) return false;

  markCanvasDirty(ctx.canvas, bounds.x, bounds.y, bounds.width, bounds.height);
  return true;
}

function commitGpuPencilCanvasToLayer(ctx, state, bounds) {
  if (!ctx || !state || !state.canvas || !bounds) return false;

  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.filter = 'none';
  ctx.shadowBlur = 0;

  if (state.supportsTransparentCanvasCopy) {
    ctx.drawImage(state.canvas, 0, 0, bounds.width, bounds.height, bounds.x, bounds.y, bounds.width, bounds.height);
    ctx.restore();
    return true;
  }

  const commitCanvas = state.commitCanvas || (typeof document !== 'undefined' ? document.createElement('canvas') : null);
  if (!commitCanvas) {
    ctx.restore();
    return false;
  }
  state.commitCanvas = commitCanvas;
  if (commitCanvas.width !== bounds.width) commitCanvas.width = bounds.width;
  if (commitCanvas.height !== bounds.height) commitCanvas.height = bounds.height;

  const commitContext = commitCanvas.getContext('2d', { willReadFrequently: true });
  if (!commitContext) {
    ctx.restore();
    return false;
  }

  const gl = state.gl;
  const width = Math.max(1, bounds.width | 0);
  const height = Math.max(1, bounds.height | 0);
  const sourcePixels = new Uint8Array(width * height * 4);

  try {
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, sourcePixels);
  } catch {
    ctx.restore();
    return false;
  }

  const imageData = commitContext.createImageData(width, height);
  const targetPixels = imageData.data;
  const rowStride = width * 4;

  for (let y = 0; y < height; y += 1) {
    const sourceOffset = (height - 1 - y) * rowStride;
    const targetOffset = y * rowStride;

    // The WebGL pencil surface is blended into a transparent framebuffer, so
    // readPixels returns premultiplied RGB. ImageData expects straight RGB.
    // Copying those bytes directly multiplies the selected colour by alpha a
    // second time when the preview canvas composites over the model, making the
    // live pencil appear much darker than the committed projection.
    for (let x = 0; x < width; x += 1) {
      const sourcePixelOffset = sourceOffset + (x * 4);
      const targetPixelOffset = targetOffset + (x * 4);
      const alphaByte = sourcePixels[sourcePixelOffset + 3];

      if (alphaByte > 0) {
        const inverseAlpha = 255 / alphaByte;
        targetPixels[targetPixelOffset] = Math.min(
          255,
          Math.round(sourcePixels[sourcePixelOffset] * inverseAlpha),
        );
        targetPixels[targetPixelOffset + 1] = Math.min(
          255,
          Math.round(sourcePixels[sourcePixelOffset + 1] * inverseAlpha),
        );
        targetPixels[targetPixelOffset + 2] = Math.min(
          255,
          Math.round(sourcePixels[sourcePixelOffset + 2] * inverseAlpha),
        );
      } else {
        targetPixels[targetPixelOffset] = 0;
        targetPixels[targetPixelOffset + 1] = 0;
        targetPixels[targetPixelOffset + 2] = 0;
      }
      targetPixels[targetPixelOffset + 3] = alphaByte;
    }
  }

  commitContext.clearRect(0, 0, width, height);
  commitContext.putImageData(imageData, 0, 0);
  ctx.drawImage(commitCanvas, 0, 0, width, height, bounds.x, bounds.y, width, height);
  ctx.restore();
  return true;
}

function renderPencilStamps(ctx, stamps, debugOptions = null) {
  const cleanStamps = Array.isArray(stamps) ? stamps.filter(Boolean) : [];
  if (!cleanStamps.length) return;
  if (renderPencilStampsWithGpu(ctx, cleanStamps, debugOptions)) return;
  for (let index = 0; index < cleanStamps.length; index += 1) {
    const stamp = cleanStamps[index];
    drawStamp(
      ctx,
      stamp.point,
      stamp.size,
      stamp.alpha,
      stamp.rotation,
      stamp.stampSeed,
      stamp.isEraser,
      stamp.scaleX,
      stamp.scaleY,
      debugOptions && (debugOptions.color || debugOptions.strokeColor),
    );
  }
}

function hash32(value) {
  let n = value | 0;
  n = Math.imul(n ^ (n >>> 16), 0x45d9f3b);
  n = Math.imul(n ^ (n >>> 16), 0x45d9f3b);
  n = n ^ (n >>> 16);
  return n >>> 0;
}

function combineSeed(...parts) {
  let seed = 0x811c9dc5;
  for (let i = 0; i < parts.length; i += 1) {
    const value = Number(parts[i]) || 0;
    seed ^= hash32(value + (i * 374761393));
    seed = Math.imul(seed, 16777619);
  }
  return seed >>> 0;
}

function randomUnitFromInts(...parts) {
  return (combineSeed(...parts) & 0xffff) / 0xffff;
}

function normaliseDegrees(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return ((n % 360) + 360) % 360;
}

function clampPencilElevation(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 90;
  return Math.max(15, Math.min(90, n));
}

function clampPencilGrade(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(-8, Math.min(8, Math.round(n)));
}

function lerp(a, b, t) {
  return a + ((b - a) * t);
}

function getPencilGradeProfile(options) {
  const grade = clampPencilGrade(options && options.pencilGrade);
  const t = (grade + 8) / 16;

  return {
    grade,
    t,
    softnessBoost: lerp(-38, 48, t),
    alphaMultiplier: lerp(0.12, 3.4, t),
    spacingMultiplier: lerp(1.85, 0.62, t),
    sizeMultiplier: lerp(0.72, 1.34, t),
    jitterMultiplier: lerp(0.18, 2.35, t),
    toothMultiplier: lerp(2.8, 0.42, t),
    tipDensity: lerp(0.42, 1.0, t),
  };
}

export function getPencilOrientation(options) {
  const orientation = options && options.pencilOrientation ? options.pencilOrientation : null;
  return {
    azimuth: normaliseDegrees(orientation && orientation.azimuth),
    elevation: clampPencilElevation(orientation && orientation.elevation),
    detected: Boolean(orientation && orientation.detected),
  };
}

export function getTiltFactor(elevation) {
  const tiltDegrees = 90 - clampPencilElevation(elevation);
  return tiltDegrees >= 30 ? 1 : 0;
}

function clampPencilPressure(value, fallback = 0.68) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0.02, Math.min(1, n));
}

function getPointPressure(point, fallback = 0.68) {
  if (!point || typeof point !== 'object') return fallback;
  return clampPencilPressure(point.pressure, fallback);
}

function getPressureResponseStart(pressure) {
  const safePressure = clampPencilPressure(pressure);
  const startInput = 0.07;
  const startOutput = 0.3;

  if (safePressure <= startInput) {
    return Math.min(1, (safePressure / startInput) * startOutput);
  }

  return Math.min(1, startOutput + (((safePressure - startInput) / (1 - startInput)) * (1 - startOutput)));
}

function getPressureAlphaMultiplier(pressure) {
  const safePressure = getPressureResponseStart(pressure);
  return 0.08 + (Math.pow(safePressure, 1.15) * 1.08);
}

function getPressureSizeMultiplier(pressure) {
  const safePressure = getPressureResponseStart(pressure);
  return 0.36 + (Math.pow(safePressure, 0.72) * 0.78);
}

export function getPencilMetrics(size, softness, options = null) {
  const profile = getPencilGradeProfile(options);
  const safeSize = Math.max(1, Number(size) || 1) * profile.sizeMultiplier;
  const safeSoftness = clampSoftness((Number(softness) || 0) + profile.softnessBoost);
  const hardness = 1 - (safeSoftness / 100);

  return {
    size: safeSize,
    lineWidth: Math.max(1, safeSize * (0.64 + hardness * 0.18)),
    spacing: Math.max(0.12, safeSize * 0.09 * profile.spacingMultiplier),
    hardness,
    gradeProfile: profile,
  };
}

function interpolatePencilOrientation(a, b, t) {
  const startOrientation = a && a.pencilOrientation ? getPencilOrientation({ pencilOrientation: a.pencilOrientation }) : null;
  const endOrientation = b && b.pencilOrientation ? getPencilOrientation({ pencilOrientation: b.pencilOrientation }) : null;

  if (!startOrientation && !endOrientation) return null;
  if (!startOrientation) return endOrientation;
  if (!endOrientation) return startOrientation;

  let deltaAzimuth = endOrientation.azimuth - startOrientation.azimuth;
  if (deltaAzimuth > 180) deltaAzimuth -= 360;
  if (deltaAzimuth < -180) deltaAzimuth += 360;

  return {
    azimuth: normaliseDegrees(startOrientation.azimuth + (deltaAzimuth * t)),
    elevation: clampPencilElevation(startOrientation.elevation + ((endOrientation.elevation - startOrientation.elevation) * t)),
    detected: Boolean(startOrientation.detected || endOrientation.detected),
  };
}

function interpolatePoint(a, b, t) {
  const point = {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    pressure: lerp(getPointPressure(a), getPointPressure(b), t),
  };
  const pencilOrientation = interpolatePencilOrientation(a, b, t);
  if (pencilOrientation) point.pencilOrientation = pencilOrientation;
  return point;
}

function getPointPencilOrientation(point, options) {
  if (point && point.pencilOrientation && typeof point.pencilOrientation === 'object') {
    return getPencilOrientation({ pencilOrientation: point.pencilOrientation });
  }
  return getPencilOrientation(options);
}

function getSmoothedPoint(points, index) {
  if (!Array.isArray(points) || points.length === 0) {
    return null;
  }

  if (points.length === 1) {
    const point = points[0];
    const nextPoint = {
      x: point.x,
      y: point.y,
      pressure: getPointPressure(point),
    };
    if (point.pencilOrientation) nextPoint.pencilOrientation = getPencilOrientation({ pencilOrientation: point.pencilOrientation });
    return nextPoint;
  }

  if (points.length === 2) {
    const point = points[index];
    if (!point) return null;
    const nextPoint = {
      x: point.x,
      y: point.y,
      pressure: getPointPressure(point),
    };
    if (point.pencilOrientation) nextPoint.pencilOrientation = getPencilOrientation({ pencilOrientation: point.pencilOrientation });
    return nextPoint;
  }

  if (index <= 0) {
    const point = points[0];
    const nextPoint = {
      x: point.x,
      y: point.y,
      pressure: getPointPressure(point),
    };
    if (point.pencilOrientation) nextPoint.pencilOrientation = getPencilOrientation({ pencilOrientation: point.pencilOrientation });
    return nextPoint;
  }

  if (index >= points.length - 1) {
    const last = points[points.length - 1];
    const nextPoint = {
      x: last.x,
      y: last.y,
      pressure: getPointPressure(last),
    };
    if (last.pencilOrientation) nextPoint.pencilOrientation = getPencilOrientation({ pencilOrientation: last.pencilOrientation });
    return nextPoint;
  }

  const current = points[index];
  const next = points[index + 1];
  return interpolatePoint(current, next, 0.5);
}

function createBrushTipCanvas(size, seed) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  const cx = size / 2;
  const cy = size / 2;
  const points = 28;
  const baseRadius = size * 0.36;

  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = '#000';
  ctx.beginPath();

  for (let i = 0; i < points; i += 1) {
    const angle = (i / points) * Math.PI * 2;
    const radialNoise = randomUnitFromInts(seed, 1001, i);
    const radius = baseRadius * (0.72 + (radialNoise * 0.42));
    const x = cx + (Math.cos(angle) * radius);
    const y = cy + (Math.sin(angle) * radius);

    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }

  ctx.closePath();
  ctx.fill();

  ctx.globalCompositeOperation = 'destination-out';

  const holeCount = 3 + Math.floor(randomUnitFromInts(seed, 2001) * 4);
  for (let i = 0; i < holeCount; i += 1) {
    const angle = randomUnitFromInts(seed, 3001, i) * Math.PI * 2;
    const dist = size * (0.06 + (randomUnitFromInts(seed, 3002, i) * 0.16));
    const hx = cx + (Math.cos(angle) * dist);
    const hy = cy + (Math.sin(angle) * dist);
    const hr = size * (0.07 + (randomUnitFromInts(seed, 3003, i) * 0.09));

    ctx.beginPath();
    ctx.ellipse(
      hx,
      hy,
      hr * (0.8 + (randomUnitFromInts(seed, 3004, i) * 0.5)),
      hr * (0.65 + (randomUnitFromInts(seed, 3005, i) * 0.45)),
      randomUnitFromInts(seed, 3006, i) * Math.PI * 2,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }

  ctx.globalCompositeOperation = 'destination-out';

  const edgeNibbleCount = 8;
  for (let i = 0; i < edgeNibbleCount; i += 1) {
    const angle = randomUnitFromInts(seed, 4001, i) * Math.PI * 2;
    const dist = baseRadius * (0.8 + (randomUnitFromInts(seed, 4002, i) * 0.25));
    const ex = cx + (Math.cos(angle) * dist);
    const ey = cy + (Math.sin(angle) * dist);
    const er = size * (0.035 + (randomUnitFromInts(seed, 4003, i) * 0.045));

    ctx.beginPath();
    ctx.arc(ex, ey, er, 0, Math.PI * 2);
    ctx.fill();
  }

  return canvas;
}

function getBrushTipCanvas(size, seed) {
  const roundedSize = Math.max(24, Math.round(size));
  const cacheKey = `${roundedSize}:${seed >>> 0}`;

  if (brushTipCache.has(cacheKey)) {
    const cached = brushTipCache.get(cacheKey);
    brushTipCache.delete(cacheKey);
    brushTipCache.set(cacheKey, cached);
    return cached;
  }

  const canvas = createBrushTipCanvas(roundedSize, seed >>> 0);

  if (brushTipCache.size >= MAX_BRUSH_TIP_CACHE_SIZE) {
    const oldestKey = brushTipCache.keys().next().value;
    if (oldestKey) {
      brushTipCache.delete(oldestKey);
    }
  }

  brushTipCache.set(cacheKey, canvas);
  return canvas;
}

export function getPencilStampTransform(options, strokeSeed = 0, dabIndex = 0, includeNoise = true, point = null) {
  const orientation = getPointPencilOrientation(point, options);
  const tiltFactor = getTiltFactor(orientation.elevation);

  const baseRotationNoise = includeNoise
    ? (randomUnitFromInts(strokeSeed, 5001, dabIndex) - 0.5) * 0.6
    : 0;
  const stylusStrokeOffset = includeNoise && orientation.detected ? 90 : 0;
  const appliedAzimuth = normaliseDegrees(orientation.azimuth + 90 + stylusStrokeOffset);
  const azimuthRadians = (appliedAzimuth * Math.PI) / 180;
  const directionalNoise = includeNoise
    ? (randomUnitFromInts(strokeSeed, 5002, dabIndex) - 0.5) * 0.08
    : 0;

  if (tiltFactor <= 0.0001) {
    return {
      rotation: baseRotationNoise,
      scaleX: 1,
      scaleY: 1,
      tiltFactor,
    };
  }

  return {
    rotation: azimuthRadians + directionalNoise,
    scaleX: 1 + (tiltFactor * 4.6),
    scaleY: Math.max(0.34, 1 - (tiltFactor * 0.58)),
    tiltFactor,
  };
}

function getStampTransform(options, strokeSeed, dabIndex, point = null) {
  return getPencilStampTransform(options, strokeSeed, dabIndex, true, point);
}

function getTiltedAlpha(alpha, tiltFactor) {
  const pressureLoss = 1 - (tiltFactor * 0.42);
  return alpha * pressureLoss;
}

function getPencilTipVariantSeed(strokeSeed, dabIndex, grade = 0) {
  const variantIndex = Math.abs(Math.floor(Number(dabIndex) || 0)) % PENCIL_TIP_VARIANTS_PER_STROKE;
  return combineSeed(strokeSeed, 8001, variantIndex, clampPencilGrade(grade) + 8);
}


export function getPencilBrushTipCanvas(size, seed) {
  return getBrushTipCanvas(size, seed);
}

export function getPencilStrokeSeed(points, options, strokeSeed = 0) {
  return buildStrokeSeed(points, options, strokeSeed);
}

function buildPencilStampDefinition(point, size, alpha, rotation, stampSeed, scaleX = 1, scaleY = 1, isEraser = false) {
  if (!point) return null;

  const pressure = Number(point.inputPressure ?? point.pressure);
  return {
    point: { x: Number(point.x) || 0, y: Number(point.y) || 0 },
    // Contact pressure for the dry media (graphite reads only point/size/alpha).
    pressure: Number.isFinite(pressure) && pressure > 0 ? Math.min(1, pressure) : null,
    size,
    alpha,
    rotation,
    stampSeed,
    scaleX,
    scaleY,
    isEraser: Boolean(isEraser),
  };
}

export function getPencilSinglePointStamp(point, options, strokeSeed = 0, dabIndex = 0) {
  if (!point) return null;

  const { size, hardness, gradeProfile } = getPencilMetrics(options.size, options.softness, options);
  const safeOpacity = clampOpacity(options.opacity);
  const transform = getStampTransform(options, strokeSeed, dabIndex, point);
  const pressure = getPointPressure(point);
  const pressureAlpha = getPressureAlphaMultiplier(pressure);
  const pressureSize = getPressureSizeMultiplier(pressure);

  const baseAlpha = safeOpacity * (0.075 + (hardness * 0.025)) * gradeProfile.alphaMultiplier * pressureAlpha;
  const alpha = Math.max(0.001, Math.min(0.72, getTiltedAlpha(baseAlpha, transform.tiltFactor) / gradeProfile.toothMultiplier));
  const stampSeed = getPencilTipVariantSeed(strokeSeed, dabIndex, gradeProfile.grade);

  return buildPencilStampDefinition(
    point,
    size * 0.9 * pressureSize,
    alpha,
    transform.rotation,
    stampSeed,
    transform.scaleX,
    transform.scaleY,
    options.isEraser,
  );
}

export function getPencilDenseSegmentStamps(start, end, options, strokeSeed, dabIndexStart = 0, shouldIncludeStart = false) {
  if (!start || !end) {
    return { stamps: [], nextDabIndex: dabIndexStart };
  }

  const { size, spacing, hardness, gradeProfile } = getPencilMetrics(options.size, options.softness, options);
  const safeOpacity = clampOpacity(options.opacity);

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.hypot(dx, dy);

  if (distance <= 0.0001) {
    if (!shouldIncludeStart) {
      return { stamps: [], nextDabIndex: dabIndexStart };
    }

    const singleStamp = getPencilSinglePointStamp(start, options, strokeSeed, dabIndexStart);
    return {
      stamps: singleStamp ? [singleStamp] : [],
      nextDabIndex: dabIndexStart + (singleStamp ? 1 : 0),
    };
  }

  const steps = Math.max(1, Math.ceil(distance / Math.max(0.0001, spacing)));
  const startStep = shouldIncludeStart ? 0 : 1;
  let dabIndex = dabIndexStart;
  const stamps = [];

  for (let step = startStep; step <= steps; step += 1) {
    const t = step / steps;
    const point = interpolatePoint(start, end, t);

    const alphaNoise = randomUnitFromInts(strokeSeed, 7001, dabIndex);
    const sizeNoise = randomUnitFromInts(strokeSeed, 7002, dabIndex);
    const jitterNoiseX = randomUnitFromInts(strokeSeed, 7004, dabIndex);
    const jitterNoiseY = randomUnitFromInts(strokeSeed, 7005, dabIndex);
    const transform = getStampTransform(options, strokeSeed, dabIndex, point);
    const pressure = getPointPressure(point);
    const pressureAlpha = getPressureAlphaMultiplier(pressure);
    const pressureSize = getPressureSizeMultiplier(pressure);

    const baseAlpha = safeOpacity
      * (0.07 + (hardness * 0.03))
      * (0.72 + (alphaNoise * 0.56))
      * gradeProfile.alphaMultiplier
      * pressureAlpha;
    const alpha = Math.max(0.0008, Math.min(0.72, getTiltedAlpha(baseAlpha, transform.tiltFactor) / gradeProfile.toothMultiplier));

    const stampSize = size * (0.88 + (sizeNoise * 0.22)) * pressureSize;
    const jitterAmountX = Math.max(0.04, size * pressureSize * (0.012 + (transform.tiltFactor * 0.045)) * gradeProfile.jitterMultiplier);
    const jitterAmountY = Math.max(0.04, size * pressureSize * 0.014 * gradeProfile.jitterMultiplier);
    const jitteredPoint = {
      x: point.x + ((jitterNoiseX - 0.5) * jitterAmountX),
      y: point.y + ((jitterNoiseY - 0.5) * jitterAmountY),
    };
    const stampSeed = getPencilTipVariantSeed(strokeSeed, dabIndex, gradeProfile.grade);

    const stamp = buildPencilStampDefinition(
      jitteredPoint,
      stampSize,
      alpha,
      transform.rotation,
      stampSeed,
      transform.scaleX,
      transform.scaleY,
      options.isEraser,
    );
    if (stamp) stamps.push(stamp);
    dabIndex += 1;
  }

  return { stamps, nextDabIndex: dabIndex };
}

function drawStamp(ctx, point, size, alpha, rotation, stampSeed, isEraser, scaleX = 1, scaleY = 1, colour = '#000000') {
  if (!ctx || !point) return;

  if (isEraser) {
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.globalAlpha = Math.max(0.01, Math.min(1, alpha));
    ctx.translate(point.x, point.y);
    ctx.rotate(rotation);
    ctx.scale(scaleX, scaleY);
    ctx.beginPath();
    ctx.arc(0, 0, size * 0.34, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }

  const tip = getBrushTipCanvas(128, stampSeed);

  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = Math.max(0.01, Math.min(1, alpha));
  ctx.translate(point.x, point.y);
  ctx.rotate(rotation);
  ctx.scale(scaleX, scaleY);
  ctx.drawImage(tip, -size / 2, -size / 2, size, size);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = typeof colour === 'string' && colour.trim() ? colour : '#000000';
  ctx.fillRect(-size / 2, -size / 2, size, size);
  ctx.restore();
}

function drawSinglePencilPoint(ctx, point, options, strokeSeed = 0, dabIndex = 0) {
  if (!ctx || !point) return;
  const stamp = getPencilSinglePointStamp(point, options, strokeSeed, dabIndex);
  if (!stamp) return;
  renderPencilStamps(ctx, [stamp], options);
}

function drawDenseSegment(ctx, start, end, options, strokeSeed, dabIndexStart, shouldIncludeStart) {
  if (!ctx || !start || !end) {
    return dabIndexStart;
  }

  const result = getPencilDenseSegmentStamps(start, end, options, strokeSeed, dabIndexStart, shouldIncludeStart);
  renderPencilStamps(ctx, result.stamps, options);
  return result.nextDabIndex;
}


function drawRawPencilTrail(ctx, points, options, strokeSeed = 0, dabIndexStart = 0, shouldIncludeStart = false) {
  if (!ctx || !Array.isArray(points) || points.length === 0) return dabIndexStart;

  if (points.length === 1) {
    if (shouldIncludeStart) {
      drawSinglePencilPoint(ctx, points[0], options, strokeSeed, dabIndexStart);
      return dabIndexStart + 1;
    }
    return dabIndexStart;
  }

  let dabIndex = dabIndexStart;
  const batchedStamps = [];

  for (let i = 1; i < points.length; i += 1) {
    const result = getPencilDenseSegmentStamps(
      points[i - 1],
      points[i],
      options,
      strokeSeed,
      dabIndex,
      i === 1 ? shouldIncludeStart : false,
    );

    if (result.stamps.length) {
      batchedStamps.push(...result.stamps);
    }
    dabIndex = result.nextDabIndex;
  }

  renderPencilStamps(ctx, batchedStamps, options);
  return dabIndex;
}

function midpointPoint(a, b) {
  return interpolatePoint(a, b, 0.5);
}

function quadraticPoint(start, control, end, t) {
  const oneMinusT = 1 - t;
  const x = (oneMinusT * oneMinusT * start.x) + (2 * oneMinusT * t * control.x) + (t * t * end.x);
  const y = (oneMinusT * oneMinusT * start.y) + (2 * oneMinusT * t * control.y) + (t * t * end.y);
  const pressure = lerp(
    lerp(getPointPressure(start), getPointPressure(control), t),
    lerp(getPointPressure(control), getPointPressure(end), t),
    t,
  );

  const startToControl = interpolatePoint(start, control, t);
  const controlToEnd = interpolatePoint(control, end, t);
  const pencilOrientation = interpolatePencilOrientation(startToControl, controlToEnd, t);

  const point = { x, y, pressure };
  if (pencilOrientation) point.pencilOrientation = pencilOrientation;
  return point;
}

function buildQuadraticPencilPoints(start, control, end, options) {
  const metrics = getPencilMetrics(options.size, options.softness, options);
  const approximateLength = Math.hypot(control.x - start.x, control.y - start.y) + Math.hypot(end.x - control.x, end.y - control.y);
  const sampleSpacing = Math.max(1.2, Math.min(7, metrics.size * 0.42));
  const steps = Math.max(2, Math.min(96, Math.ceil(approximateLength / sampleSpacing)));
  const points = [];

  for (let i = 0; i <= steps; i += 1) {
    points.push(quadraticPoint(start, control, end, i / steps));
  }

  return points;
}

function drawDenseGraphiteTrail(ctx, points, options, strokeSeed = 0, dabIndexStart = 0, shouldIncludeStart = true) {
  if (!ctx || !Array.isArray(points) || points.length === 0) return dabIndexStart;

  if (points.length === 1) {
    drawSinglePencilPoint(ctx, points[0], options, strokeSeed, dabIndexStart);
    return dabIndexStart + 1;
  }

  let dabIndex = dabIndexStart;
  let previousSmoothedPoint = getSmoothedPoint(points, 0);
  const batchedStamps = [];

  for (let i = 1; i < points.length; i += 1) {
    const currentSmoothedPoint = getSmoothedPoint(points, i);
    if (!previousSmoothedPoint || !currentSmoothedPoint) {
      previousSmoothedPoint = currentSmoothedPoint;
      continue;
    }

    const result = getPencilDenseSegmentStamps(
      previousSmoothedPoint,
      currentSmoothedPoint,
      options,
      strokeSeed,
      dabIndex,
      i === 1 ? shouldIncludeStart : false,
    );

    if (result.stamps.length) {
      batchedStamps.push(...result.stamps);
    }
    dabIndex = result.nextDabIndex;
    previousSmoothedPoint = currentSmoothedPoint;
  }

  renderPencilStamps(ctx, batchedStamps, options);
  return dabIndex;
}

function buildStrokeSeed(points, options, strokeSeed = 0) {
  const first = Array.isArray(points) && points[0] ? points[0] : { x: 0, y: 0 };
  const orientation = getPointPencilOrientation(first, options);

  return combineSeed(
    strokeSeed,
    Math.round((first.x || 0) * 100),
    Math.round((first.y || 0) * 100),
    Math.round((Number(options.size) || 0) * 100),
    Math.round((Number(options.softness) || 0) * 100),
    Math.round((Number(options.opacity) || 0) * 100),
    Math.round(clampPencilGrade(options.pencilGrade) * 100),
    Math.round(orientation.azimuth * 10),
    Math.round(orientation.elevation * 10),
  );
}

export function drawPencilStroke(ctx, points, options) {
  const strokeSeed = buildStrokeSeed(points, options, 0);
  drawDenseGraphiteTrail(ctx, points, options, strokeSeed, 0, true);
}

export function drawPencilStrokeWithinSelection(ctx, points, options, selection) {
  if (!ctx) return;

  if (!selection || !selection.bounds) {
    drawPencilStroke(ctx, points, options);
    return;
  }

  const clipped = clipContextToSelection(ctx, selection);
  drawPencilStroke(ctx, points, options);
  if (clipped) {
    ctx.restore();
  }
}


export function drawPencilCurveSegmentWithinSelection(
  ctx,
  previousPreviousPoint,
  previousPoint,
  currentPoint,
  options,
  selection,
  strokeSeed,
  dabIndexStart = 0,
  shouldIncludeStart = false,
) {
  if (!ctx || !previousPreviousPoint || !previousPoint || !currentPoint) return dabIndexStart;

  const curveStart = midpointPoint(previousPreviousPoint, previousPoint);
  const curveEnd = midpointPoint(previousPoint, currentPoint);
  const points = buildQuadraticPencilPoints(curveStart, previousPoint, curveEnd, options);
  const effectiveSeed = buildStrokeSeed([curveStart, previousPoint, curveEnd], options, strokeSeed);

  if (!selection || !selection.bounds) {
    return drawRawPencilTrail(ctx, points, options, effectiveSeed, dabIndexStart, shouldIncludeStart);
  }

  const clipped = clipContextToSelection(ctx, selection);
  const nextDabIndex = drawRawPencilTrail(ctx, points, options, effectiveSeed, dabIndexStart, shouldIncludeStart);
  if (clipped) {
    ctx.restore();
  }
  return nextDabIndex;
}

export function drawPencilFinalTailWithinSelection(
  ctx,
  previousPoint,
  currentPoint,
  options,
  selection,
  strokeSeed,
  dabIndexStart = 0,
) {
  if (!ctx || !previousPoint || !currentPoint) return dabIndexStart;

  const tailStart = midpointPoint(previousPoint, currentPoint);
  const points = [tailStart, currentPoint];
  const effectiveSeed = buildStrokeSeed(points, options, strokeSeed);

  if (!selection || !selection.bounds) {
    return drawRawPencilTrail(ctx, points, options, effectiveSeed, dabIndexStart, false);
  }

  const clipped = clipContextToSelection(ctx, selection);
  const nextDabIndex = drawRawPencilTrail(ctx, points, options, effectiveSeed, dabIndexStart, false);
  if (clipped) {
    ctx.restore();
  }
  return nextDabIndex;
}

export function drawPencilSegmentWithinSelection(
  ctx,
  startPoint,
  endPoint,
  options,
  selection,
  strokeSeed,
  dabIndexStart = 0,
  shouldIncludeStart = false,
) {
  if (!ctx) return dabIndexStart;

  const points = endPoint ? [startPoint, endPoint] : [startPoint];
  const effectiveSeed = buildStrokeSeed(points, options, strokeSeed);

  if (!selection || !selection.bounds) {
    return drawDenseGraphiteTrail(ctx, points, options, effectiveSeed, dabIndexStart, shouldIncludeStart);
  }

  const clipped = clipContextToSelection(ctx, selection);
  const nextDabIndex = drawDenseGraphiteTrail(ctx, points, options, effectiveSeed, dabIndexStart, shouldIncludeStart);
  if (clipped) {
    ctx.restore();
  }
  return nextDabIndex;
}